<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <b-modal
      v-model="modal.show"
      :title="modalTitle"
      :ok-title="`Submit`"
      :ok-disabled="okButtonDisabled"
      @hidden="onHideModal"
      @ok.prevent="handleSubmit(onSubmit)"
    >
      <show-error
        :show="errorExist"
        :text="firstError"
      />
      <content-spinner :show="loading" />
      <b-form
        v-if="!loading"
        @keydown.enter.prevent="handleSubmit(onSubmit)"
      >
        <input-widget
          v-for="field in fields"
          :key="`${field.name}-${updateComponent}`"
          :model="form"
          :field="field"
          :autofocus="field.autofocus"
          :disabled="!field.data.options.length"
          @change="e => onFieldChange(e, field.name)"
        />
      </b-form>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../../../core/components/ContentSpinner';
import InputWidget from '../../../../core/components/complex-table/components/InputWidget';
import ShowError from '../../../../core/components/ShowError';

const {mapState, mapActions} = createNamespacedHelpers('fileManipulationController');
const {mapState: mapConnectorState} = createNamespacedHelpers('connectorScreen');

export default {
  name: 'AddEditBatchEndpointChildModal',
  data() {
    return {
      loading: false,
      errors: {
        endpointAlreadyExist: {
          show: false,
          message: 'Endpoint already exist',
        },
      },
      form: {
        configurationId: null,
        endpointId: null,
      },
      fields: {
        configurationId: {
          type: 'select',
          label: 'Connector',
          required: true,
          name: 'configurationId',
          data: {
            options: [],
          },
        },
        endpointId: {
          type: 'multi-select',
          label: 'Endpoint',
          required: true,
          name: 'endpointId',
          data: {
            options: [],
          },
          config: {
            optionValue: 'value',
            optionLabel: 'text',
            placeholder: 'Endpoints',
            multiple: true,
            taggable: false,
          },
        },
      },
      updateComponent: 0,
    };
  },
  computed: {
    ...mapState({
      modal: state => state.addEditBatchEndpointChildModal,
    }),
    ...mapConnectorState({
      connectors: state => state.connectors,
      pitchBookEndpoints: state => state.startingPoint.pitchBookEndpoints,
    }),
    modalTitle() {
      return 'Add Batch Endpoint';
    },
    errorExist() {
      return Object.keys(this.errors).some(errorName => this.errors[errorName].show);
    },
    firstError() {
      let errorMessage = null;

      for (const errorName of Object.keys(this.errors)) {
        if (this.errors[errorName].show) {
          errorMessage = this.errors[errorName].message;
          break;
        }
      }

      return errorMessage;
    },
    okButtonDisabled() {
      return this.loading || this.errorExist;
    },
  },
  methods: {
    ...mapActions([
      'hideAddEditBatchEndpointChildModal',
    ]),
    validateModalData() {
      if (this.modal.data.batchEndpoints) {
        /**
         * Check if endpoint from selected connector already exist in batchEndpoints list.
         */
        if (this.modal.data.batchEndpoints.find(endpoint => `${endpoint.configurationId}|${endpoint.endpointId}` === `${this.form.configurationId}|${this.form.endpointId}`)) {
          this.errors.endpointAlreadyExist.show = true;
          return false;
        } else {
          this.errors.endpointAlreadyExist.show = false;
        }
      }

      return true;
    },
    onSubmit() {
      if (!this.validateModalData()) {
        return false;
      }

      this.$emit('submit', JSON.parse(JSON.stringify(this.form)));
      this.hideAddEditBatchEndpointChildModal();
    },
    onHideModal() {
      this.clearForm();
      this.hideError();
    },
    onFieldChange(value, field) {
      const isConnectorField = field === 'configurationId';

      if (isConnectorField) this.prepareEndpointFieldOptions(value);

      this.validateModalData();
    },
    clearForm() {
      this.form.endpointId = this.form.configurationId = null;
      this.fields.endpointId.data.options = [];
    },
    hideError() {
      Object.keys(this.errors).forEach(errorName => this.errors[errorName].show = false);
    },
    prepareData() {
      this.prepareConnectorFieldOptions();
    },
    prepareConnectorFieldOptions() {
      this.fields.configurationId.data.options = this.connectors.map(connector => {
        const credentials = JSON.parse(connector.credentials);
        return {
          value: connector.configurationId,
          text: `${connector.vendor} - ${credentials.Tenant}`,
        };
      });
    },
    prepareEndpointFieldOptions(configurationId) {
      const connector = this.connectors.find(connector => connector.configurationId === configurationId);

      let options;
      if (this.isPitchBook(connector)) {
        options = Object.keys(connector.startingPoints).map(startingPointId => {
          const startingPoint = connector.startingPoints[startingPointId];

          return {
            value: startingPoint.startingPointId,
            text: this.pitchBookEndpoints[startingPoint.entityName] || startingPoint.startingPointId,
          };
        });
      } else {
        options = connector.endpoints.map(endpoint => {
          const endpointConfigurationJSON = JSON.parse(endpoint.endpointConfigurationJSON);

          const tableName = this.isSnowflake(connector) ? endpointConfigurationJSON.tableName : null;

          return {
            value: endpoint.endpointId,
            text: endpoint.friendlyName || tableName || endpoint.endpointId,
          };
        });
      }

      this.fields.endpointId.data.options = options;

      this.updateComponent++;
    },
    isSnowflake(connector) {
      return connector.vendor === 'Snowflake';
    },
    isPitchBook(connector) {
      return connector.vendor === 'PitchBook';
    },
  },
  mounted() {
    this.prepareData();
  },
  components: {
    ContentSpinner,
    InputWidget,
    ShowError,
  },
};
</script>

<style scoped>

</style>
